import { useCallback, useMemo, useState } from 'react';
import { AxiosError } from 'axios';
import { ModalRef } from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import api from 'api';
import { getAxiosErrorMessageCode } from 'axiosUtils';
import { ERROR_TYPES } from 'request-responses';
import { ObjectValues } from 'types';
import useCallbackRef from './useCallbackRef';

export const PHONE_TRANSFER_STATE = {
  IDLE: 'idle',
  // The number is in use on another account - offer to claim it by SMS.
  CONFLICT: 'conflict',
  // Three failed attempts on this number in the last 24h, across accounts.
  LIMIT_REACHED: 'limit',
} as const;

export type PhoneTransferState = ObjectValues<typeof PHONE_TRANSFER_STATE>;

export const PHONE_TRANSFER_MODE = {
  // Claimant has no account yet, so the public auth routes are used and the
  // resulting phoneClaimToken is replayed into auth/signup.
  SIGNUP: 'signup',
  // Claimant is signed in, so the classic authenticated pair does the work and
  // the profile PATCH is replayed once the number is proven.
  PROFILE: 'profile',
} as const;

export type PhoneTransferMode = ObjectValues<typeof PHONE_TRANSFER_MODE>;

type UsePhoneNumberTransferProps = {
  mode: PhoneTransferMode;
};

export default function usePhoneNumberTransfer(
  props: UsePhoneNumberTransferProps,
) {
  const { mode } = props;

  const [state, setState] = useState<PhoneTransferState>(
    PHONE_TRANSFER_STATE.IDLE,
  );
  const [contactNumber, setContactNumber] = useState<string>();

  const [modal, modalRef] = useCallbackRef<ModalRef>();

  const isSignup = mode === PHONE_TRANSFER_MODE.SIGNUP;

  const reset = useCallback(() => {
    setState(PHONE_TRANSFER_STATE.IDLE);
    setContactNumber(undefined);
  }, []);

  /**
   * Claims the conflict/limit errors this flow knows how to render inline.
   * Returns false for anything else so the caller can fall back to its usual
   * `popServerError` toast.
   */
  const onServerError = useCallback(
    (error: AxiosError | unknown, number?: string) => {
      const messageCode = getAxiosErrorMessageCode(error as AxiosError);

      if (messageCode === ERROR_TYPES.PHONE_NUMBER_IN_USE_ON_ANOTHER_ACCOUNT) {
        setContactNumber(number);
        setState(PHONE_TRANSFER_STATE.CONFLICT);
        return true;
      }

      if (messageCode === ERROR_TYPES.PHONE_VERIFICATION_BLOCKED) {
        setContactNumber(number);
        setState(PHONE_TRANSFER_STATE.LIMIT_REACHED);
        modal?.close();
        return true;
      }

      return false;
    },
    [modal],
  );

  // Signed-in claimants go through the same routes an ordinary verification
  // uses; only the signup path needs the public pair, since there is no account
  // to authenticate with yet.
  const initiate = useCallback(
    (number: string) =>
      isSignup
        ? api.auth.initiatePhoneVerification(number)
        : api.users.phoneVerification(number),
    [isSignup],
  );

  const check = useCallback(
    (number: string, verificationCode: string) =>
      isSignup
        ? api.auth.confirmPhoneVerification(number, verificationCode)
        : api.users.checkPhoneVerification(verificationCode),
    [isSignup],
  );

  const openModal = useCallback(() => modal?.open(), [modal]);

  return useMemo(
    () => ({
      state,
      contactNumber,
      setContactNumber,
      isConflict: state === PHONE_TRANSFER_STATE.CONFLICT,
      isLimitReached: state === PHONE_TRANSFER_STATE.LIMIT_REACHED,
      modal,
      modalRef,
      openModal,
      initiate,
      check,
      onServerError,
      reset,
    }),
    [
      state,
      contactNumber,
      modal,
      modalRef,
      openModal,
      initiate,
      check,
      onServerError,
      reset,
    ],
  );
}
